import axios, { HttpStatusCode, type InternalAxiosRequestConfig } from 'axios';

import { apiBaseUrl } from '@/helpers/apiBaseUrl';
import CustomCache from '@/helpers/cache';
import { isTokenExpired } from '@/modules/auth/helpers/jwtUtils';
import {
	ensureFreshToken,
	forceLogout,
} from '@/modules/auth/helpers/tokenRefresh';
import authService from '@/modules/auth/services/authService';

// Endpoints publics qui peuvent renvoyer 401 de maniere legitime sans devoir
// declencher de retry ou de deconnexion automatique (login, refresh,
// echange de code logas).
const AUTH_ENDPOINT_REGEX =
	/login|refresh-token|logas\/validate|decode-token|active-proposals\/response/i;

export const setupInterceptors = () => {
	const isDevelopment = import.meta.env.VITE_ENV === 'local';

	if (isDevelopment) {
		console.log('💿 Application cache enabled', import.meta.env.VITE_ENV);
	}
	const cache = new CustomCache('local');

	axios.interceptors.request.use(async (request) => {
		const url = request.url;
		const isWizimAPI =
			url?.startsWith(import.meta.env.VITE_API_URL) ||
			(apiBaseUrl === '/v1' && url?.startsWith?.('/'));

		if (
			url &&
			request.method?.toLowerCase() === 'get' &&
			isDevelopment &&
			isWizimAPI
		) {
			const cachedData = cache.getCacheData(url, request.params);
			if (cachedData) {
				request.data = cachedData;
				request.adapter = () => {
					return Promise.resolve({
						data: cachedData,
						status: 200,
						statusText: 'OK',
						headers: request.headers,
						config: request,
						request: request,
					});
				};
				return request;
			}
		}

		const tokens = authService.getTokens();

		if (tokens && url && !AUTH_ENDPOINT_REGEX.test(url)) {
			let accessToken = tokens.access_token;

			if (isTokenExpired(accessToken, 30_000)) {
				accessToken = await ensureFreshToken();
			}

			request.headers.Authorization = `Bearer ${accessToken}`;
		}
		return request;
	});

	axios.interceptors.response.use(
		(response) => {
			const url = response.config.url;
			const isWizimAPI =
				url?.startsWith(import.meta.env.VITE_API_URL) ||
				(apiBaseUrl === '/v1' && url?.startsWith?.('/'));
			if (
				response.status.toString().startsWith('2') &&
				isDevelopment &&
				isWizimAPI
			) {
				const method = response.config.method?.toLowerCase();
				if (!method) return response;

				if (method === 'get' && response.data && url) {
					cache.addCacheData(url, response.data, response.config.params);
				}
				if (['post', 'put', 'patch', 'delete'].includes(method) && url) {
					cache.deleteCacheData(url);
				}
			}
			return response;
		},
		async (error) => {
			const originalRequest = error.config as InternalAxiosRequestConfig & {
				_retried?: boolean;
			};
			const response = error.response;

			if (
				response?.status === HttpStatusCode.Unauthorized &&
				originalRequest &&
				!originalRequest._retried &&
				!AUTH_ENDPOINT_REGEX.test(originalRequest.url ?? '')
			) {
				originalRequest._retried = true;

				try {
					const freshToken = await ensureFreshToken();
					originalRequest.headers.Authorization = `Bearer ${freshToken}`;
					return axios(originalRequest);
				} catch {
					return Promise.reject(error);
				}
			}

			if (
				response?.status === HttpStatusCode.Unauthorized &&
				originalRequest?._retried &&
				!AUTH_ENDPOINT_REGEX.test(originalRequest.url ?? '')
			) {
				await forceLogout();
			}

			return Promise.reject(error);
		}
	);
};
